import * as React from 'react';
import {
  useLocation,
  UNSAFE_LocationContext as ContextoLocalizacao,
  UNSAFE_RouteContext as ContextoRota,
} from 'react-router-dom';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { navConfigAdminPlus, navConfigAdmin, navConfigCliente, ConfigNav } from '../navbar/NavConfig';

/* ============================================================================
   Abas com ESTADO PRESERVADO.

   A primeira versao desta barra apenas navegava: trocar de aba remontava a tela e
   buscava tudo de novo. Agora as telas abertas ficam MONTADAS ao mesmo tempo e as
   inativas sao escondidas - filtro digitado, pagina da tabela e formulario pela
   metade continuam onde estavam.

   ---------------------------------------------------------------------------
   O PROBLEMA CENTRAL: tela montada e tela VIVA.

   Uma tela escondida continua no ar e continua re-renderizando. Se ela le a URL,
   passa a ler a URL da aba ATIVA - e uma tela de detalhe montada com o codigo 12,
   ao trocar de aba, veria o endereco de outra tela e poderia buscar o registro
   errado. Num sistema com dispositivo e politica reais isso nao e detalhe visual.

   Medido antes de escrever este arquivo:
     - nenhuma tela em pages/dashboard usa useParams/useSearchParams/useLocation;
     - MAS 7 telas usam `useRouter` do shim src/compat/next-router.tsx, e ele le
       useLocation + useParams + useSearchParams por dentro.

   A SOLUCAO e generica, e nao tela por tela: cada aba guarda os contextos de rota
   que o react-router deu a ela quando foi montada, e os reprovê para a sua
   subarvore. Assim a aba escondida continua vendo o SEU proprio endereco, para
   sempre. Nenhuma das 7 telas precisou ser tocada, e telas futuras ja nascem
   protegidas.

   Os contextos sao capturados INTEIROS com useContext e reprovidos como vieram -
   sem remontar o objeto a mao. Escrever `{ location, navigationType: 'POP' }`
   dependeria do formato interno do react-router e quebraria calado numa
   atualizacao dele.

   `UNSAFE_` no nome e aviso de API interna, nao de gambiarra: e assim que o
   react-router expoe seus contextos para quem precisa deste tipo de controle. O
   risco real e a assinatura mudar entre versoes maiores - por isso a captura
   integral acima, que e o que torna isto sobrevivel.
   ---------------------------------------------------------------------------

   LIMITE DE 10 ABAS: agora ele tem um segundo motivo, alem de nao poluir a barra.
   Cada aba aberta e uma tela montada de verdade, com os dados dela na memoria.
   Ao passar de 10, a mais antiga fora de uso sai e o cache dela e descartado -
   reabrir busca de novo, que e o certo.
   ============================================================================ */

const CHAVE = 'gpshub:abas';
const MAXIMO = 10;

export type Aba = { caminho: string; rotulo: string };

type Registro = {
  elemento: React.ReactNode;
  ctxLocalizacao: unknown;
  ctxRota: unknown;
};

type ValorAbas = {
  abas: Aba[];
  fechar: (caminho: string) => void;
  destinoAoFechar: (caminho: string) => string;
};

const AbasContexto = React.createContext<ValorAbas | null>(null);

const CacheContexto = React.createContext<React.MutableRefObject<Map<string, Registro>> | null>(null);

/* Diz a subarvore se ELA e a aba visivel. Componentes com efeito GLOBAL (travar o
   scroll do body, escutar tecla na window) precisam saber: uma aba escondida com
   modal aberto continuaria travando o scroll da aba ativa.

   O padrao e `true` para que quem esta fora das abas (login, telas sem layout de
   dashboard) funcione sem precisar de provider. */
const AbaAtivaContexto = React.createContext(true);

export const useAbaAtiva = () => React.useContext(AbaAtivaContexto);

export function useAbas(): ValorAbas {
  const valor = React.useContext(AbasContexto);
  if (!valor) throw new Error('useAbas precisa estar dentro de <AbasProvider>');
  return valor;
}

/* ---------------------------------------------------------------------------
   Rotulo "Grupo > Tela", lido do proprio menu: renomear um item no menu renomeia
   a aba, sem segunda lista para manter.

   Le os TRES configs porque o rotulo nao depende do perfil - o caminho de grupos
   se chama "Grupos" para todo mundo. Ler so o do perfil atual deixaria sem nome as
   telas que aquele perfil alcanca por link direto.
   --------------------------------------------------------------------------- */
const MAPA: Record<string, string> = (() => {
  const mapa: Record<string, string> = {};
  const grupos: ConfigNav[] = [...navConfigAdminPlus, ...navConfigAdmin, ...navConfigCliente];
  const maiuscula = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

  grupos.forEach((grupo) => {
    grupo.items.forEach((item) => {
      // O pai entra primeiro: quando ele tem filhos, o primeiro filho aponta para
      // o mesmo caminho ("Listar") e o nome do pai e o mais informativo.
      if (!mapa[item.path]) mapa[item.path] = `${maiuscula(grupo.subheader)} > ${item.title}`;

      (item.children ?? []).forEach((filho) => {
        if (!mapa[filho.path]) {
          mapa[filho.path] = `${maiuscula(grupo.subheader)} > ${item.title}: ${filho.title}`;
        }
      });
    });
  });

  return mapa;
})();

/** Caminhos que existem so para mandar a pessoa a outro lugar. Nao sao telas,
    entao nao abrem aba. */
const SO_REDIRECIONAM = new Set(['/dashboard']);

export function rotuloDoCaminho(caminho: string): string {
  if (MAPA[caminho]) return MAPA[caminho];

  // Herda do PAI: o detalhe de um dispositivo continua sendo "Dispositivos".
  const partes = caminho.split('/').filter(Boolean);
  for (let i = partes.length - 1; i > 1; i -= 1) {
    const pai = `/${partes.slice(0, i).join('/')}`;
    if (MAPA[pai]) return MAPA[pai];
  }

  const ultima = partes[partes.length - 1] ?? 'Tela';
  return ultima
    .replace(/-novo$/, '')
    .replace(/-/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());
}

function ler(): Aba[] {
  try {
    const bruto = sessionStorage.getItem(CHAVE);
    const lista = bruto ? JSON.parse(bruto) : [];
    return Array.isArray(lista) ? lista.filter((a) => a && typeof a.caminho === 'string') : [];
  } catch {
    // sessionStorage pode falhar (modo restrito). As abas funcionam sem ele, so
    // nao sobrevivem ao F5.
    return [];
  }
}

/* ============================================================================
   Provider - fica ACIMA das rotas, em main.tsx.

   Nao pode ficar dentro do DashboardLayout: a lista de abas e o cache das telas
   precisam sobreviver a qualquer remontagem do layout. Aqui em cima elas
   sobrevivem por construcao.
   ============================================================================ */
export function AbasProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [abas, setAbas] = React.useState<Aba[]>(ler);

  // O cache vive em ref, e nao em estado: gravar nele NAO deve provocar render, e
  // ele e escrito durante o render da area de telas.
  const cache = React.useRef(new Map<string, Registro>());

  React.useEffect(() => {
    if (!pathname.startsWith('/dashboard')) return;

    /* DUAS ABAS AO ENTRAR (corrigido em 10/09/2026).

       Depois do login apareciam "Dashboard" e "App". A primeira nao era tela
       nenhuma: `/dashboard` e um stub que so mostra a tela de carregando e
       redireciona para `/dashboard/app` (ver pages/dashboard/index.jsx). Como o
       registro de aba olha so o pathname, ele guardava o caminho de passagem
       junto com o destino - e o rotulo "Dashboard" saia do fallback, que usa o
       ultimo segmento da URL quando nao ha entrada no menu.

       Caminho que apenas redireciona nao e tela e nao abre aba. Clicar na aba
       "Dashboard" tambem nao levava a lugar nenhum: ela voltava a redirecionar
       e a aba ficava piscando. */
    if (SO_REDIRECIONAM.has(pathname.replace(/\/$/, '') || '/')) return;

    setAbas((atuais) => {
      if (atuais.some((a) => a.caminho === pathname)) return atuais;

      const proximas = [...atuais, { caminho: pathname, rotulo: rotuloDoCaminho(pathname) }];
      if (proximas.length <= MAXIMO) return proximas;

      const descartavel = proximas.findIndex((a) => a.caminho !== pathname);
      if (descartavel === -1) return proximas;

      // Descartar a aba tem que descartar a TELA tambem, senao a memoria dela
      // fica presa sem nada apontando para a aba.
      cache.current.delete(proximas[descartavel].caminho);
      return proximas.filter((_, i) => i !== descartavel);
    });
  }, [pathname]);

  React.useEffect(() => {
    try {
      sessionStorage.setItem(CHAVE, JSON.stringify(abas));
    } catch {
      /* sem sessionStorage as abas valem so para esta visita */
    }
  }, [abas]);

  const valor = React.useMemo<ValorAbas>(
    () => ({
      abas,

      fechar: (caminho: string) => {
        cache.current.delete(caminho);
        setAbas((atuais) => atuais.filter((a) => a.caminho !== caminho));
      },

      /* Para onde ir ao fechar a aba ATUAL: a vizinha da esquerda, como em
         navegador. Fechar sem sair deixaria a tela na frente sem aba marcada, e a
         barra passaria a mentir sobre onde a pessoa esta. */
      destinoAoFechar: (caminho: string) => {
        const indice = abas.findIndex((a) => a.caminho === caminho);
        const restantes = abas.filter((a) => a.caminho !== caminho);
        return restantes[Math.max(0, indice - 1)]?.caminho ?? PATH_DASHBOARD.general.app;
      },
    }),
    [abas]
  );

  return (
    <AbasContexto.Provider value={valor}>
      <CacheContexto.Provider value={cache}>{children}</CacheContexto.Provider>
    </AbasContexto.Provider>
  );
}


/* ============================================================================
   Area das telas - substitui o `{children}` do DashboardLayout.

   Renderiza TODAS as abas abertas, escondendo as inativas. A posicao de cada uma
   e fixada pela `key` do caminho: e isso que preserva o estado. Se a aba ativa
   fosse renderizada num lugar e as inativas em outro, trocar de aba mudaria a
   posicao na arvore, o React remontaria o componente e o estado morreria - que e
   justamente o que esta versao existe para evitar.
   ============================================================================ */
export function AreaTelas({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { abas } = useAbas();
  const cache = React.useContext(CacheContexto);

  /* ROLAGEM POR ABA.

     O scroll pertence a JANELA, nao a cada tela - esconder uma aba com `display:
     none` nao guarda a posicao dela. Sem isto, voltar para uma aba longa cairia no
     topo, e cair no topo e exatamente o que parece "recarregou".

     Guarda a posicao da aba que esta SAINDO e restaura a da que entra. Caminho
     visto pela primeira vez nao tem posicao guardada e comeca em zero, que e o
     comportamento normal de abrir uma tela. */
  const rolagens = React.useRef<Record<string, number>>({});
  const anterior = React.useRef(pathname);

  React.useEffect(() => {
    if (anterior.current !== pathname) {
      rolagens.current[anterior.current] = window.scrollY;
      anterior.current = pathname;
      window.scrollTo(0, rolagens.current[pathname] ?? 0);
    }
  }, [pathname]);

  // Contextos que o react-router deu a ESTA tela, capturados inteiros.
  const ctxLocalizacao = React.useContext(ContextoLocalizacao);
  const ctxRota = React.useContext(ContextoRota);

  const dentroDoDashboard = pathname.startsWith('/dashboard');

  // Fora do dashboard nao ha abas: a tela vai direto, sem cache nenhum.
  if (!dentroDoDashboard || !cache) return <>{children}</>;

  // Grava a tela atual. Acontece no render de proposito: no primeiro render de um
  // caminho novo o efeito do provider ainda nao rodou, e esperar por ele deixaria
  // a tela em branco por um quadro.
  cache.current.set(pathname, { elemento: children, ctxLocalizacao, ctxRota });

  /* A aba atual entra na lista MESMO antes de o provider registra-la, pelo mesmo
     motivo - e com a mesma `key`, entao quando o registro chega nao ha
     remontagem. */
  const lista = abas.some((a) => a.caminho === pathname)
    ? abas
    : [...abas, { caminho: pathname, rotulo: rotuloDoCaminho(pathname) }];

  return (
    <>
      {lista.map((aba) => {
        const registro = cache.current.get(aba.caminho);
        if (!registro) return null;

        const ativa = aba.caminho === pathname;

        return (
          <div
            key={aba.caminho}
            /* `contents` e nao `block`: o <main> e flex-col e as telas eram filhas
               diretas dele. Uma div comum aqui viraria um item flex no meio e
               mudaria o layout de todas as telas. Com `contents` o wrapper
               desaparece para efeito de layout. */
            style={{ display: ativa ? 'contents' : 'none' }}
            aria-hidden={!ativa}
          >
            <ContextoLocalizacao.Provider value={registro.ctxLocalizacao as any}>
              <ContextoRota.Provider value={registro.ctxRota as any}>
                <AbaAtivaContexto.Provider value={ativa}>{registro.elemento}</AbaAtivaContexto.Provider>
              </ContextoRota.Provider>
            </ContextoLocalizacao.Provider>
          </div>
        );
      })}
    </>
  );
}
